import type { NextPage } from 'next'
import Image from 'next/image'
import {
  IBlogFields,
  IBlog,
  IFooterFields,
  IHeaderFields,
  IBlogTag,
} from '../../@types/generated/contentful'
import Layout from '../../components/layout'
import RichText from '../../components/richText'
import BlogPostAuthor from '../../components/blog/blogPostAuthor'
import SharePage from '../../components/sharePage'
import BlogRelatedPosts from '../../components/blog/blogRelatedPosts'
import BlogDivider from '../../components/blog/blogDivider'
import { ContentfulService } from '../../services/contentful'
import { CONTENT_TYPE_BLOG } from '../../services/constants'

type Props = {
  page: IBlogFields
  header: IHeaderFields
  footer: IFooterFields
  relatedPosts?: IBlog[] | null
}

const Page: NextPage<Props> = ({
  page,
  header,
  footer,
  relatedPosts,
}: Props) => {
  const { date, summary, title, content, slug, meta, image } = page

  return (
    <Layout
      title={title}
      slug={slug}
      meta={meta.fields}
      header={header}
      footer={footer}
    >
      <div className="container mx-auto my-5 px-4 md:my-6 md:px-0 lg:my-14">
        <article>
          <div className="grid grid-cols-12">
            <div className="order-2 col-span-12 flex items-center justify-center md:order-1 md:col-span-6 md:px-2">
              <div className="lg:py-6">
                <h1 className="insight-title py-5 lg:pb-6">{title}</h1>
                <BlogPostAuthor meta={meta.fields} date={date} />
              </div>
            </div>
            <div className="order-1 col-span-12 md:order-2 md:col-span-6">
              <Image
                src={`https:${image.fields.file.url}`}
                alt={image.fields.description}
                width={image.fields.file.details.image?.width}
                height={image.fields.file.details.image?.height}
                layout="responsive"
                priority
              />
            </div>
          </div>
          <BlogDivider />
          <div className="grid grid-cols-12">
            <div className="col-span-12 grid md:col-span-8">
              <div className="flex flex-col">
                <span className="body-medium pb-2 font-bold">{summary}</span>
                <RichText content={content} />
              </div>
            </div>
            <div className="col-span-12 grid md:col-span-3 md:col-start-10">
              <div className="flex flex-col">
                <SharePage
                  title={title}
                  url={`${process.env.NEXT_PUBLIC_CANONICAL_URL}/insights/${slug}`}
                />
                <BlogRelatedPosts relatedPosts={relatedPosts} />
              </div>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  )
}

export async function getServerSideProps(context: any) {
  const contentfulService = new ContentfulService()

  const { slug } = context.query

  try {
    const responses = await Promise.all([
      contentfulService.getEntryBySlug(slug, CONTENT_TYPE_BLOG),
      contentfulService.getHeader(),
      contentfulService.getFooter(),
    ])
    if (!responses || !responses[0]) throw new Error('Page not found')

    const tags = responses[0].fields.blogTag.map((tag: IBlogTag) => {
      return tag.sys.id
    })
    const relatedPosts = await contentfulService.getRelatedBlogPosts(
      tags,
      responses[0].fields.slug
    )

    return {
      props: {
        page: responses[0].fields,
        header: responses[1]?.fields,
        footer: responses[2]?.fields,
        relatedPosts: relatedPosts || [],
      },
    }
  } catch (e) {
    return {
      notFound: true,
    }
  }
}

export default Page
